import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { companyApi, TwoFactorSettingsData, TwoFactorComplianceData, TwoFactorComplianceUser } from 'shared/services/companyApi';
import { ApiResponse } from 'shared/services/apiClient';
import { queryKeys } from '../queryConfig';

/**
 * Hook to fetch 2FA settings for a company
 *
 * @param companyId - Company ID
 * @param enabled - Whether the query should run (default true)
 * @returns React Query result with 2FA settings data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useTwoFactorSettings('company-123');
 * const settings = data?.data;
 * ```
 */
export const useTwoFactorSettings = (
  companyId: string,
  enabled = true
): UseQueryResult<ApiResponse<TwoFactorSettingsData>, Error> => {
  return useQuery({
    queryKey: queryKeys.twoFactor.settings(companyId),
    queryFn: async () => {
      const response = await companyApi.getTwoFactorSettings(companyId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch 2FA settings');
      }

      return response;
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000,        // 5 minutes
    gcTime: 10 * 60 * 1000,          // 10 minutes
    retry: 1,
  });
};

/**
 * Hook to fetch 2FA compliance report for a company
 *
 * Shows which users are compliant with 2FA requirements.
 *
 * @param companyId - Company ID
 * @param enabled - Whether the query should run (default true)
 * @returns React Query result with compliance data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTwoFactorCompliance('company-123');
 * const compliance = data?.data;
 * console.log(`${compliance?.compliancePercentage}% compliant`);
 * ```
 */
// Extended compliance data with computed fields for UI compatibility
export interface TwoFactorComplianceUIData extends TwoFactorComplianceData {
  // Computed fields for UI display
  totalUsers: number;
  compliantUsers: number;
  nonCompliantUsers: number;
  compliancePercentage: number;
  // Non-compliant users list for quick access
  nonCompliantUsersList: TwoFactorComplianceData['users'];
  // Group users by role for display
  usersByRole: Array<{
    role: string;
    total: number;
    compliant: number;
  }>;
}

export const useTwoFactorCompliance = (
  companyId: string,
  enabled = true
): UseQueryResult<ApiResponse<TwoFactorComplianceUIData>, Error> => {
  return useQuery({
    queryKey: queryKeys.twoFactor.compliance(companyId),
    queryFn: async () => {
      const response = await companyApi.getTwoFactorCompliance(companyId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch 2FA compliance');
      }

      // Transform response to include computed fields for UI compatibility
      const data = response.data;
      const nonCompliantUsersList = data.users?.filter((u: TwoFactorComplianceUser) => !u.isCompliant) || [];

      // Group users by role
      const roleMap = new Map<string, { total: number; compliant: number }>();
      for (const user of data.users || []) {
        const existing = roleMap.get(user.role) || { total: 0, compliant: 0 };
        existing.total++;
        if (user.isCompliant) existing.compliant++;
        roleMap.set(user.role, existing);
      }
      const usersByRole = Array.from(roleMap.entries()).map(([role, stats]) => ({
        role,
        ...stats,
      }));

      const transformedData: TwoFactorComplianceUIData = {
        ...data,
        // Add computed fields
        totalUsers: data.compliance?.total || 0,
        compliantUsers: data.compliance?.compliant || 0,
        nonCompliantUsers: data.compliance?.nonCompliant || 0,
        compliancePercentage: data.compliance?.complianceRate || 0,
        nonCompliantUsersList,
        usersByRole,
      };

      return {
        ...response,
        data: transformedData,
      };
    },
    enabled: enabled && !!companyId,
    staleTime: 2 * 60 * 1000,        // 2 minutes - compliance changes frequently
    gcTime: 5 * 60 * 1000,           // 5 minutes
    retry: 1,
  });
};
